import { v4 as uuid } from 'uuid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
dayjs.extend(utc);
dayjs.extend(timezone);
import { OrderItem, OrderItemOption, OrderItemSlot, OrderItemSlotItem } from "./OrderItem.js";
import { DeliveryZoneCountryZipCodeRule } from "./DeliveryZone.js";
import { Product, ProductType } from "./Product.js";
import { CartItem } from "./CartItem.js";
import { CartBreakdown } from "./CartBreakdown.js";
import { OrderRequest } from "./OrderRequest.js";
import { ErrorCode } from "./ErrorCode.js";
import { DeliveryType, PaymentType } from "./Enums.js";
export { PaymentType } from "./Enums.js";
import { alwaysOpenTimeTableRules, findTimeTableForCompany, UNRESTRICTED_TIME_TABLE_ID } from "./TimeTable.js";
import { ProductionSchedule, ScheduleItem, ScheduleStep, ScheduleWindow } from "./ProductionSchedule.js";
export class OrderCalculator {
    company;
    orderItems = [];
    orderRequest;
    customer = null;
    constructor(company, orderRequest, customer) {
        this.company = company;
        this.orderRequest = orderRequest;
        if (customer) {
            this.customer = customer;
        }
        if (this.orderRequest.cartItems && this.orderRequest.cartItems.length > 0) {
            for (let cartItem of this.orderRequest.cartItems) {
                let orderItem = this.convertCartItem(cartItem);
                if (orderItem) {
                    this.orderItems.push(orderItem);
                }
            }
        }
    }
    validate() {
        if (!this.company.settings) {
            console.error('@OrderCalculator#validate: Called without settings');
            return {};
        }
        let errors = {};
        if (!this.orderRequest.deliveryType) {
            errors['deliveryType'] = { code: ErrorCode.missing };
        }
        if (this.orderRequest.deliveryType && !Object.keys(DeliveryType).includes(this.orderRequest.deliveryType)) {
            errors['deliveryType'] = { code: ErrorCode.invalidValue };
        }
        if (!this.orderRequest.zoneId) {
            errors['zoneId'] = { code: ErrorCode.missing };
        }
        if (!this.orderRequest.pointofsaleId) {
            errors['zoneId'] = { code: ErrorCode.missing };
        }
        let zone = this.company.getSalesZoneById(this.orderRequest.zoneId);
        if (!zone) {
            errors['zoneId'] = { code: ErrorCode.invalidValue };
        }
        else {
            let pointofsale = zone.getPointOfSale(this.orderRequest.pointofsaleId);
            if (!pointofsale) {
                errors['pointofsaleId'] = { code: ErrorCode.invalidValue };
            }
        }
        if (zone && zone.collectBillingAddress) {
            if (!this.orderRequest.billingAddress) {
                errors['billingAddress'] = { code: ErrorCode.missing };
            }
            if (this.orderRequest.billingAddress && !this.orderRequest.billingAddress.isComplete()) {
                errors['billingAddress'] = { code: ErrorCode.incompleteValue };
            }
        }
        if (this.orderRequest.deliveryType == DeliveryType.delivery) {
            //if the zone collects billing addresses we assume its just incomplete for now and don't raise an additional error
            let hasBillingAddress = (zone && zone.collectBillingAddress) || this.orderRequest.billingAddress && this.orderRequest.billingAddress.isComplete();
            if (!hasBillingAddress && (!this.orderRequest.deliveryAddress || !this.orderRequest.deliveryAddress.isComplete())) {
                console.log('this.orderRequest.deliveryAddress', this.orderRequest.deliveryAddress);
                errors['deliveryAddress'] = { code: ErrorCode.incompleteValue };
            }
        }
        if (!this.orderRequest.paymentType) {
            errors['paymentType'] = { code: ErrorCode.missing };
        }
        if (this.orderRequest.paymentType == PaymentType.offline && !this.company.settings.enableOfflinePayments) {
            errors['paymentType'] = { code: ErrorCode.invalidValue };
        }
        const isBackendOrder = Boolean(this.orderRequest.userId);
        let companyIsOpen = Boolean(this.company.settings.isOpen);
        let salesZoneIsOpen = Boolean(zone && !zone.hidden && !zone.closed);
        //only check if the company is open if its a request from the website and not from the backend
        // its an error if any of the two is closed. 
        if (!isBackendOrder && (!salesZoneIsOpen || !companyIsOpen)) {
            if (!companyIsOpen) {
                errors['closed'] = { code: ErrorCode.companyIsClosed };
            }
            if (!salesZoneIsOpen) {
                errors['closed'] = { code: ErrorCode.salesZoneIsClosed };
            }
        }
        //only check POS payment method if it is no backend order 
        if (!isBackendOrder && zone) {
            if (this.orderRequest.paymentType == PaymentType.offline && !zone.enableOfflinePayment) {
                console.error('zone doesnt allow offlinepayment');
                errors['paymentType'] = { code: ErrorCode.invalidValue };
            }
            if (this.orderRequest.paymentType == PaymentType.online && !zone.enableOnlinePayment) {
                errors['paymentType'] = { code: ErrorCode.invalidValue };
            }
        }
        // //check if it has an address if it's above the simplifiedInvoiceTreshold
        // let cartBreakDown = this.getCartBreakDown()
        // let simplifiedInvoiceTreshold = 400
        // if(cartBreakDown && cartBreakDown.total > simplifiedInvoiceTreshold && (!this.orderRequest.billingAddress || !this.orderRequest.billingAddress.isComplete()) ){
        //     console.log('Billing address needed bacause of simplificada Limit')
        //     errors['billingAddress'] = { code: ErrorCode.missing }
        // }
        return Object.keys(errors).length > 0 ? errors : null;
    }
    /**
     * Compute the unit price (with option corrections, slot corrections)
     * and line subtotal for a single order item.
     * Returns null when the product is missing or the item is invalid — callers should skip it.
     * The optional `infos` array collects human-readable names from options/slots
     * for callers that need to display a line description.
     */
    getOrderItemLine(item, infos) {
        if (!item || !item.quantity || !item.productId)
            return null;
        let product = this.company.getProductById(item.productId);
        if (!product)
            return null;
        let price = product.price;
        for (let option of (item.options || [])) {
            for (let value of (option.values || [])) {
                if (infos && value.name)
                    infos.push(value.name);
                if (value.correction) {
                    price += value.correction;
                }
            }
        }
        if (product.type == ProductType.combined && product.slots && product.slots.length > 0 && item.chosen) {
            for (let slot of product.slots) {
                let orderItemSlot = item.chosen[slot.id];
                if (!orderItemSlot)
                    continue; //TODO make sure its only an empty slot
                let chosenSlotItem = orderItemSlot.chosen;
                if (infos)
                    infos.push(chosenSlotItem.name);
                if (chosenSlotItem && chosenSlotItem.correction) {
                    price += Number.parseFloat("" + chosenSlotItem.correction);
                }
            }
        }
        return { product, price, subtotal: item.quantity * price };
    }
    /**
     * Resolve all discounts that apply to the current cart, deduped:
     * zone auto-applied + customer auto-applied + discounts attached to the orderRequest
     * (set by backend user or by voucher code). Missing discount IDs are skipped silently.
     */
    getApplicableDiscounts() {
        let allDiscountIds = [];
        let zone = this.company.getSalesZoneById(this.orderRequest.zoneId);
        if (zone && zone.autoAppliedDiscounts && zone.autoAppliedDiscounts.length > 0) {
            allDiscountIds = allDiscountIds.concat(zone.autoAppliedDiscounts);
        }
        if (this.customer && this.customer.autoAppliedDiscounts && this.customer.autoAppliedDiscounts.length > 0) {
            allDiscountIds = allDiscountIds.concat(this.customer.autoAppliedDiscounts);
        }
        if (this.orderRequest.discounts && this.orderRequest.discounts.length > 0) {
            allDiscountIds = allDiscountIds.concat(this.orderRequest.discounts);
        }
        if (allDiscountIds.length == 0)
            return [];
        let discounts = [];
        for (let discountId of [...new Set(allDiscountIds)]) {
            let discount = this.company.getDiscountById(discountId);
            if (!discount)
                continue;
            discounts.push(discount);
        }
        return discounts;
    }
    /**
     * Subtotal of the cart before delivery costs and tip — sum of item subtotals (gross)
     * minus auto-applied/voucher discounts. Matches cartBreakDown.subtotal at the point
     * the delivery zone is selected, so it can be used for delivery-bracket selection
     * without recomputing the full breakdown.
     */
    getCombinedSubtotal() {
        let subtotal = 0;
        for (let item of this.orderItems) {
            let line = this.getOrderItemLine(item);
            if (!line)
                continue;
            subtotal += line.subtotal;
        }
        for (let discount of this.getApplicableDiscounts()) {
            let percentage = Number.parseFloat('' + discount.percentage);
            subtotal = subtotal - subtotal / 100 * percentage;
        }
        return subtotal;
    }
    getCombinedWeight() {
        let weight = 0;
        // console.log('@Cart#getWeight:this.orderItems', this.orderItems)
        for (let item of this.orderItems) {
            if (!item || !item.quantity) {
                console.error('@Cart#getWeight: Item not found or no quantity', item);
                continue;
            }
            if (!item.productId) {
                console.error('@Cart#getWeight: Item without productId', item);
                continue;
            }
            let product = this.company.getProductById(item.productId);
            if (!product) {
                console.error('@Cart#getWeight: product not found', item);
                continue;
            }
            if (!product.weight) {
                continue;
            }
            let productWeight = Number.parseFloat('' + product.weight);
            let quantity = Number.parseInt('' + item.quantity);
            weight += productWeight * quantity;
        }
        return weight;
    }
    /**
     * all delivery zones which could serve the order's address, each with the
     * price bracket applicable to the current cart (zones whose brackets can't
     * cover the cart are left out)
     */
    getApplicableDeliveryZones() {
        let cartWeight = this.getCombinedWeight();
        let cartSubtotal = this.getCombinedSubtotal();
        if (this.orderRequest.deliveryType != DeliveryType.delivery) {
            return [];
        }
        if (!this.company) {
            console.error('@Cart#getApplicableDeliveryZone: No company found', this.company);
            return [];
        }
        if (!this.company.settings) {
            console.error('@Cart#getApplicableDeliveryZone: No settings found', this.company);
            return [];
        }
        if (!this.company.settings.deliveryZones) {
            console.error('@Cart#getApplicableDeliveryZone: No  deliveryzones found', this.company);
            return [];
        }
        let addressToUse = this.orderRequest.deliveryAddress ? this.orderRequest.deliveryAddress : this.orderRequest.billingAddress;
        if (!addressToUse) {
            console.error('@Cart#getApplicableDeliveryZone: No deliveryAddress found');
            return [];
        }
        if (!addressToUse.countryCode) {
            console.error('applicableDeliveryZone: No country code found', addressToUse);
            return [];
        }
        if (!addressToUse.zip) {
            console.error('applicableDeliveryZone: No zip found', addressToUse);
            return [];
        }
        let applicableZonesForCountryAndZip = [];
        //find all zones which match the country and zip settings
        for (let zone of this.company.settings.deliveryZones) {
            if (zone.countries) {
                for (let countryRule of zone.countries) {
                    if (countryRule.code == addressToUse.countryCode) {
                        if (countryRule.validInZipCodesRule == DeliveryZoneCountryZipCodeRule.all) {
                            applicableZonesForCountryAndZip.push(zone);
                            continue;
                        }
                        let cleanupZip = (str) => {
                            return str.trim().replaceAll(' ', '');
                        };
                        let cleanedUpZips = [];
                        if (countryRule.validInZipCodesConfig) {
                            cleanedUpZips = ('' + countryRule.validInZipCodesConfig).split(',').map(cleanupZip);
                        }
                        addressToUse.zip = cleanupZip(addressToUse.zip);
                        let configIncludesZip = false;
                        for (let zipTemplate of cleanedUpZips) {
                            //definitly right
                            if (zipTemplate == addressToUse.zip) {
                                configIncludesZip = true;
                                break;
                            }
                            //definitly wrong
                            if (zipTemplate.length != addressToUse.zip.length) {
                                continue;
                            }
                            let patternMatched = true;
                            for (let i = 0; i < zipTemplate.length; i++) {
                                let zipCharAtThisPos = addressToUse.zip.charAt(i);
                                let templateCharAtThisPos = zipTemplate.charAt(i);
                                if (zipCharAtThisPos != templateCharAtThisPos && templateCharAtThisPos != '*') {
                                    patternMatched = false;
                                    break;
                                }
                            }
                            if (patternMatched) {
                                configIncludesZip = true;
                                break;
                            }
                        }
                        if (countryRule.validInZipCodesRule == 'only' && configIncludesZip) {
                            applicableZonesForCountryAndZip.push(zone);
                        }
                        if (countryRule.validInZipCodesRule == 'except' && !configIncludesZip) {
                            applicableZonesForCountryAndZip.push(zone);
                        }
                    }
                }
            }
        }
        //price every matching zone with its cheapest bracket covering the cart
        let applicableZones = [];
        for (let zone of applicableZonesForCountryAndZip) {
            let priceOfZone = null;
            let lowestPricetWhichCoversTheCart = null;
            if (zone.prices) {
                for (let priceBracket of zone.prices) {
                    if ((priceBracket.price == null || priceBracket.price == undefined || isNaN(Number.parseFloat('' + priceBracket.price)))) {
                        continue;
                    }
                    //if no maxweight is given treat it as a very high number
                    //so price brackets with a set max weight have a chance to be chosen
                    let maxWeight = priceBracket.maxWeight ? Number.parseFloat('' + priceBracket.maxWeight) : Number.MAX_SAFE_INTEGER;
                    //if a minSubtotal is set, the cart's subtotal has to be at or above it
                    if (priceBracket.minSubtotal != null && !isNaN(Number.parseFloat('' + priceBracket.minSubtotal))) {
                        let minSubtotal = Number.parseFloat('' + priceBracket.minSubtotal);
                        if (cartSubtotal < minSubtotal) {
                            continue;
                        }
                    }
                    if ((maxWeight >= cartWeight) &&
                        (lowestPricetWhichCoversTheCart == null || Number.parseFloat('' + priceBracket.price) < lowestPricetWhichCoversTheCart)) {
                        lowestPricetWhichCoversTheCart = Number.parseFloat('' + priceBracket.price);
                        priceOfZone = Number.parseFloat('' + priceBracket.price);
                    }
                }
            }
            //zone without a matching pricebracket
            if (priceOfZone === null) {
                continue;
            }
            applicableZones.push({ zone, price: priceOfZone, weight: cartWeight });
        }
        return applicableZones;
    }
    /**
     * the delivery zone used for the order: the one the user explicitly chose
     * (orderRequest.deliveryZoneId) if it is applicable, the cheapest otherwise
     */
    getApplicableDeliveryZone() {
        let applicableZones = this.getApplicableDeliveryZones();
        if (applicableZones.length < 1) {
            return null;
        }
        if (this.orderRequest.deliveryZoneId) {
            let chosenZone = applicableZones.find((entry) => entry.zone && entry.zone.id == this.orderRequest.deliveryZoneId);
            if (chosenZone) {
                return chosenZone;
            }
        }
        let cheapestZone = applicableZones[0];
        for (let entry of applicableZones) {
            if ((entry.price || 0) < (cheapestZone.price || 0)) {
                cheapestZone = entry;
            }
        }
        return cheapestZone;
    }
    /**
     * all pickup locations of the company (the `pickupLocations` setting)
     */
    getPickupLocations() {
        let pickupLocations = this.company.settings && Array.isArray(this.company.settings.pickupLocations) ? this.company.settings.pickupLocations : [];
        return pickupLocations;
    }
    /**
     * the pickup location used for the order: the one the user explicitly chose
     * (orderRequest.pickupLocationId) if it exists, the first one otherwise
     * (null when the company has no pickup locations at all)
     */
    getChosenPickupLocation() {
        let pickupLocations = this.getPickupLocations();
        if (pickupLocations.length < 1) {
            return null;
        }
        if (this.orderRequest.pickupLocationId) {
            let chosenLocation = pickupLocations.find((location) => location && location.id == this.orderRequest.pickupLocationId);
            if (chosenLocation) {
                return chosenLocation;
            }
        }
        return pickupLocations[0];
    }
    /**
     * whether the customer may select a specific pickup time — a property of the
     * chosen pickup location. companies without pickup locations allow it (the
     * historic default from before the flag moved off the sales zone).
     */
    allowsCustomPickupTime() {
        let pickupLocation = this.getChosenPickupLocation();
        if (!pickupLocation) {
            return true;
        }
        return Boolean(pickupLocation.allowCustomPickupTime);
    }
    /**
     * takes a cart item retrieves every bit of information neeeded and saves everything into an orderitem
     * which is very similar but includes taxrates, prices, names,...
     * so the order item can be displayed correctly (as at the time of order) even if the shops configuration has changed over time
     * @param cartItem
     * @returns an OrderItem with all required information set
     */
    convertCartItem(cartItem) {
        let orderItem = new OrderItem(cartItem.id);
        if (!cartItem || !cartItem.quantity) {
            console.error('Item not found or no quantity', cartItem);
            return null;
        }
        if (!cartItem.productId) {
            console.error('@Cart#convertCartItem: no productId', cartItem);
            return null;
        }
        orderItem.quantity = cartItem.quantity;
        orderItem.productId = cartItem.productId;
        orderItem.courseId = cartItem.courseId;
        orderItem.note = cartItem.note;
        let product = this.company.getProductById(cartItem.productId);
        if (!product) {
            console.error('@Cart#convertCartItem: Product not found', cartItem.productId, cartItem);
            return null;
        }
        if (!product.price) {
            console.error('@Cart#convertCartItem: no price', cartItem, product);
            return null;
        }
        if (!this.company.settings) {
            console.error('@Cart#convertCartItem: No settings found', this.company);
            return null;
        }
        if (!this.company.settings.vatRates) {
            console.error('@Cart#convertCartItem: No vatRates found', this.company.settings);
            return null;
        }
        orderItem.name = product.name;
        orderItem.mayBeReturned = product.mayBeReturned;
        //fall back to the product's default course when the cart item carries no
        //explicit courseId (per-serving course assignment was removed on the
        //frontend) so kitchen/receipt grouping by course still works.
        if (!orderItem.courseId) {
            orderItem.courseId = product.defaultCourseId || null;
        }
        orderItem.basePrice = product.price;
        let combinedPrice = product.price;
        //get the correct vat rate for that cart item
        //thats the default vat rate
        orderItem.vatRate = this.company.settings.vat ? Number.parseFloat('' + this.company.settings.vat) : 0;
        //if the product has a custom vat rate set and we find it apply that            
        if (product.vatRate) {
            // console.log('settings', this.company.settings)
            let customVatRate = this.company.settings.getVatRateById(product.vatRate);
            if (customVatRate) {
                orderItem.vatRate = Number.parseFloat('' + customVatRate.rate);
            }
        }
        //convert all options
        if (cartItem.options && Object.values(cartItem.options).length > 0) {
            for (let optionId in cartItem.options) {
                let option = product.getOptionById(optionId);
                if (!option) {
                    continue;
                }
                let valueIds = CartItem.getOptionValueIds(cartItem.options[optionId]);
                let optionValues = [];
                for (let valueId of valueIds) {
                    let value = option.getValueById(valueId);
                    if (!value) {
                        continue;
                    }
                    let correction = value.correction && Number.parseFloat('' + value.correction) ? Number.parseFloat('' + value.correction) : 0;
                    combinedPrice += correction;
                    optionValues.push({
                        id: value.id,
                        name: value.name,
                        correction: correction
                    });
                }
                if (optionValues.length === 0)
                    continue;
                orderItem.options.push(new OrderItemOption(option.id, {
                    id: option.id,
                    name: option.name ? option.name : null,
                    values: optionValues
                }));
            }
        }
        if (product && product.type == ProductType.combined && product.slots && product.slots.length > 0 && cartItem.chosen) {
            for (let slot of product.slots) {
                let slotItemId = cartItem.chosen[slot.id] ? cartItem.chosen[slot.id] : null;
                if (!slotItemId) {
                    console.error("@Cart#convertCartItem: no product selected for slot", slot.id);
                    continue;
                }
                let chosenSlotItem = slot.getSlotItemById(slotItemId);
                if (!chosenSlotItem) {
                    console.error("@Cart#convertCartItem: Slot doesn't include slotitem with id", slot.id, slotItemId);
                    continue;
                }
                if (chosenSlotItem.hidden) {
                    console.error("@Cart#convertCartItem: SlotItem is hidden", slot.id, slotItemId);
                    continue;
                }
                let productOfLine = this.company.getProductById(chosenSlotItem.productId);
                if (!productOfLine) {
                    console.error("@Cart#convertCartItem: Selected Product not found", slot.id, slotItemId);
                    continue;
                }
                let correction = chosenSlotItem.correction && Number.parseFloat('' + chosenSlotItem.correction) ? Number.parseFloat('' + chosenSlotItem.correction) : 0;
                combinedPrice += correction;
                orderItem.chosen[slot.id] = new OrderItemSlot(slot.id, slot.name, new OrderItemSlotItem(productOfLine.name, slotItemId, correction, productOfLine.locationId || null, productOfLine.defaultCourseId || null));
                console.log('orderItem.chosen[slot.id]', orderItem.chosen[slot.id]);
            }
        }
        orderItem.price = combinedPrice;
        return orderItem;
    }
    getCartBreakDown() {
        if (!this.company.products) {
            console.error('@Cart#getCartBreakDown: No products found', this.company);
            return null;
        }
        if (!this.company.settings || !this.company.settings.vatRates) {
            console.error('@Cart#getCartBreakDown: No settings or vatRates found', this.company.settings);
            return null;
        }
        let cartBreakDown = new CartBreakdown();
        for (let item of this.orderItems) {
            let infos = [];
            let line = this.getOrderItemLine(item, infos);
            if (!line)
                continue;
            let { product, price, subtotal } = line;
            cartBreakDown.grossAmount += subtotal;
            let net = (subtotal / (item.vatRate + 100)) * 100;
            let vat = subtotal - net;
            cartBreakDown.netAmount += net;
            let vatRateKey = Number.parseFloat('' + item.vatRate).toFixed(2);
            if (!cartBreakDown.vatByRate[vatRateKey]) {
                cartBreakDown.vatByRate[vatRateKey] = 0;
            }
            cartBreakDown.vatByRate[vatRateKey] += vat;
            cartBreakDown.items[item.id] = {
                productId: product.id,
                quantity: item.quantity,
                net: net,
                price: price,
                subtotal: subtotal,
                vatRate: item.vatRate,
                vat: vat,
                name: product.name,
                infos: infos,
                mayBeReturned: item.mayBeReturned
            };
        }
        for (let discount of this.getApplicableDiscounts()) {
            let percentage = Number.parseFloat('' + discount.percentage);
            let discountAmount = cartBreakDown.grossAmount / 100 * percentage;
            cartBreakDown.grossAmount = cartBreakDown.grossAmount - discountAmount;
            cartBreakDown.netAmount = cartBreakDown.netAmount - cartBreakDown.netAmount / 100 * percentage;
            for (let vatRate in cartBreakDown.vatByRate) {
                cartBreakDown.vatByRate[vatRate] = cartBreakDown.vatByRate[vatRate] / 100 * (100 - percentage);
            }
            cartBreakDown.appliedDiscounts.push({
                name: discount.name,
                percentage: discount.percentage,
                amount: discountAmount,
                id: discount.id
            });
        }
        cartBreakDown.subtotal = cartBreakDown.total = cartBreakDown.grossAmount;
        let deliveryZoneInfo = this.getApplicableDeliveryZone();
        cartBreakDown.weight = deliveryZoneInfo && deliveryZoneInfo.weight || null;
        cartBreakDown.deliveryZoneId = deliveryZoneInfo && deliveryZoneInfo.zone && deliveryZoneInfo.zone.id || null;
        cartBreakDown.deliveryTimeNeeded = deliveryZoneInfo && deliveryZoneInfo.zone && deliveryZoneInfo.zone.averageDeliveryTime || null;
        if (this.orderRequest.deliveryType == DeliveryType.delivery && deliveryZoneInfo && deliveryZoneInfo.price) {
            cartBreakDown.deliveryCosts = deliveryZoneInfo.price;
            let vatRateForDelivery = Number.parseFloat('' + this.company.settings.vat);
            let vatForDelivery = (cartBreakDown.deliveryCosts / (100 + vatRateForDelivery)) * vatRateForDelivery;
            cartBreakDown.netAmount += (cartBreakDown.deliveryCosts - vatForDelivery);
            cartBreakDown.grossAmount += cartBreakDown.deliveryCosts;
            cartBreakDown.total = cartBreakDown.grossAmount;
            let vatRateKey = Number.parseFloat('' + vatRateForDelivery).toFixed(2);
            if (!cartBreakDown.vatByRate[vatRateKey]) {
                cartBreakDown.vatByRate[vatRateKey] = 0;
            }
            cartBreakDown.vatByRate[vatRateKey] += vatForDelivery;
        }
        //has to be at the very end because the total logic depends on it
        if (this.company.settings.tipIsEnabled && this.orderRequest.tipPercentage && !isNaN(this.orderRequest.tipPercentage) && this.orderRequest.tipPercentage > 0) {
            cartBreakDown.tip = (cartBreakDown.subtotal / 100 * this.orderRequest.tipPercentage);
            if (this.company.settings.tipIsTaxed && this.company.settings.vatForTips) {
                cartBreakDown.tipIsTaxed = true;
                let vatRateForTip = Number.parseFloat('' + this.company.settings.vatForTips);
                cartBreakDown.grossAmount += cartBreakDown.tip;
                cartBreakDown.total = cartBreakDown.grossAmount;
                let tipWithoutVat = cartBreakDown.tip / (100 + vatRateForTip) * 100;
                let vatOfTip = cartBreakDown.tip - tipWithoutVat;
                cartBreakDown.netAmount += tipWithoutVat;
                let vatRateKey = Number.parseFloat('' + vatRateForTip).toFixed(2);
                if (!cartBreakDown.vatByRate[vatRateKey]) {
                    cartBreakDown.vatByRate[vatRateKey] = 0;
                }
                cartBreakDown.vatByRate[vatRateKey] += vatOfTip;
            }
            else {
                cartBreakDown.total = cartBreakDown.grossAmount + cartBreakDown.tip;
            }
        }
        cartBreakDown.vat = cartBreakDown.grossAmount - cartBreakDown.netAmount;
        return cartBreakDown;
    }
    // ------------------------------------------------------------------
    // production & delivery timing
    // ------------------------------------------------------------------
    //converts a { value, unit } duration (unit = days | hours | minutes) into minutes, 0 if not set
    static durationInMinutes(duration) {
        let value = duration && duration.value ? Number.parseFloat(duration.value) : 0;
        if (!value || Number.isNaN(value)) {
            return 0;
        }
        let unit = duration.unit ? duration.unit : 'days';
        return value * ((unit == 'days') ? 24 * 60 : (unit == 'hours') ? 60 : 1);
    }
    timeStringToDate(dayDate, timeString) {
        let day = dayjs(dayDate).startOf('day');
        let startParts = ('' + timeString).split(':');
        if (startParts.length != 2) {
            return null;
        }
        day = day.hour(Number.parseInt(startParts[0]));
        day = day.minute(Number.parseInt(startParts[1]));
        return day;
    }
    //resolves the weekday rules of an hours config into concrete start/end dates for the given day
    //(in the company's timezone, like the customer app and functions/slots.js do)
    getApplicableHoursForDay(date, hoursConfig) {
        if (!date || !hoursConfig) {
            return [];
        }
        let companyTimezone = this.company.settings && this.company.settings.timezone ? this.company.settings.timezone : null;
        let day = companyTimezone ? dayjs(date).tz(companyTimezone).startOf('day') : dayjs(date).startOf('day');
        let weekday = day.day();
        let hours = [];
        let applicableHours = null;
        for (let rule of hoursConfig) {
            if (rule && rule.occurrence == 'periodic' && rule.weekday == weekday) {
                applicableHours = rule.hours;
                break;
            }
        }
        if (applicableHours && applicableHours.length > 0) {
            for (let range of applicableHours) {
                let startDate = this.timeStringToDate(day, range.start);
                let endDate = this.timeStringToDate(day, range.end);
                if (!startDate || !endDate) {
                    console.error('invalid date range in ', applicableHours);
                    continue;
                }
                //plain dates, not dayjs instances: this package bundles its own dayjs
                //copy, and a foreign dayjs leaking to the apps breaks their plugin setup
                //(their dayjs() returns the foreign clone instead of wrapping it)
                hours.push({ startDate: startDate.toDate(), endDate: endDate.toDate() });
            }
        }
        //the stored ranges are in no guaranteed order: sort them so "first fitting range" logic works
        hours.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
        return hours;
    }
    //resolves global tables, the local tables of the delivery zones and pickup locations
    //as well as the implicit always open one
    getTimeTableById(timeTableId) {
        return findTimeTableForCompany(this.company.settings, timeTableId);
    }
    //resolving a day's hours involves timezone math (Intl) — the callers loop over
    //many days and are re-invoked on every render, so the results are memoized.
    //safe because the company data never changes within a calculator instance.
    timeTableHoursCache = {};
    /**
     * the whole day as one range (in the company's timezone) — used when a
     * delivery zone explicitly has no time table limitation
     */
    getUnrestrictedHoursForDay(date) {
        let cacheKey = UNRESTRICTED_TIME_TABLE_ID + ':' + dayjs(date).valueOf();
        if (this.timeTableHoursCache[cacheKey]) {
            return this.timeTableHoursCache[cacheKey];
        }
        let hours = this.getApplicableHoursForDay(date, alwaysOpenTimeTableRules());
        this.timeTableHoursCache[cacheKey] = hours;
        return hours;
    }
    //unknown or empty time tables fall back to the opening hours
    getTimeTableHoursForDay(date, timeTableId) {
        let cacheKey = (timeTableId || '') + ':' + dayjs(date).valueOf();
        if (this.timeTableHoursCache[cacheKey]) {
            return this.timeTableHoursCache[cacheKey];
        }
        let timeTable = this.getTimeTableById(timeTableId);
        if (!timeTable || !Array.isArray(timeTable.rules) || timeTable.rules.length < 1) {
            timeTable = this.getTimeTableById('openingHours');
        }
        let hours = null;
        if (timeTable && Array.isArray(timeTable.rules) && timeTable.rules.length > 0) {
            hours = this.getApplicableHoursForDay(date, timeTable.rules);
        }
        else {
            let openingHours = this.company.settings && this.company.settings.openingHours ? this.company.settings.openingHours : null;
            hours = this.getApplicableHoursForDay(date, openingHours);
        }
        this.timeTableHoursCache[cacheKey] = hours;
        return hours;
    }
    /**
     * fits a duration into the time table and returns the end date plus the
     * windows (chunks of reserved time) it occupies along the way
     *
     * the duration starts at startDate at the earliest
     *
     * splitIntoMultipleWindows = true: the duration accumulates across time table windows
     * splitIntoMultipleWindows = false: the whole duration has to fit into one single window
     * (endDate is null if it can't be fitted within the next 75 days)
     */
    fitDurationIntoTimeTable(durationInMinutes, timeTableId, splitIntoMultipleWindows, startDate) {
        startDate = dayjs(startDate);
        let loopedDate = dayjs(startDate);
        let accountedForMinutes = 0;
        let windows = [];
        let numberOfDaysTried = 0;
        let maxDaysToTry = 75;
        // loop day per day and look for the first time table window which is big enough
        // to accommodate the (remaining) duration
        while (numberOfDaysTried < maxDaysToTry) {
            let tableHours = this.getTimeTableHoursForDay(loopedDate, timeTableId);
            if (tableHours && tableHours.length > 0) {
                for (let range of tableHours) {
                    let startTime = dayjs(range.startDate);
                    let endTime = dayjs(range.endDate);
                    // as we are getting the hours of the whole day, which might be the start day,
                    // we have to check if the end and/or start of the range is before the start date
                    if (startTime.isBefore(startDate)) {
                        if (endTime.isBefore(startDate)) {
                            continue;
                        }
                        else {
                            startTime = dayjs(startDate);
                        }
                    }
                    //the amount of available time in the window
                    let availableMinutes = endTime.diff(startTime, 'minutes');
                    //the amount of time we still need to fit somewhere in
                    let stillNeededMinutes = durationInMinutes - accountedForMinutes;
                    if (availableMinutes >= stillNeededMinutes) {
                        let endDate = startTime.add(stillNeededMinutes, 'minutes');
                        if (stillNeededMinutes > 0) {
                            windows.push(new ScheduleWindow({ start: startTime.toDate(), end: endDate.toDate(), minutes: stillNeededMinutes }));
                        }
                        return { endDate, windows };
                    }
                    if (splitIntoMultipleWindows && availableMinutes > 0) {
                        accountedForMinutes += availableMinutes;
                        windows.push(new ScheduleWindow({ start: startTime.toDate(), end: endTime.toDate(), minutes: availableMinutes }));
                    }
                }
            }
            numberOfDaysTried++;
            loopedDate = loopedDate.add(1, 'day');
        }
        return { endDate: null, windows: [] };
    }
    /**
     * the production times stored on the product itself, as a normalized list of steps
     * (products saved before the productionTimes array still carry the single
     * productionTime + productionTimeTableId combo: convert it into one entry)
     */
    static ownProductionTimeEntriesForProduct(product) {
        let entries = [];
        if (!product) {
            return entries;
        }
        if (product.productionTimes && Array.isArray(product.productionTimes) && product.productionTimes.length > 0) {
            for (let productionTime of product.productionTimes) {
                if (!productionTime) {
                    continue;
                }
                entries.push({
                    minutes: OrderCalculator.durationInMinutes(productionTime.duration),
                    timeTableId: productionTime.timeTableId ? productionTime.timeTableId : 'openingHours',
                    source: 'product',
                });
            }
        }
        else if (product.productionTime) {
            entries.push({
                minutes: OrderCalculator.durationInMinutes(product.productionTime),
                timeTableId: product.productionTimeTableId ? product.productionTimeTableId : 'openingHours',
                source: 'product',
            });
        }
        return entries;
    }
    /**
     * how long the given entries take altogether (they run consecutively)
     */
    static totalProductionTimeMinutes(entries) {
        if (!entries || entries.length < 1) {
            return 0;
        }
        return entries.reduce((total, entry) => total + (entry && entry.minutes ? entry.minutes : 0), 0);
    }
    /**
     * of several possible entry lists the one that takes the longest altogether
     * (ties keep the first list, so the parent product wins over its subproducts)
     */
    static longestProductionTimeEntries(candidates) {
        let longest = [];
        let longestMinutes = -1;
        for (let candidate of candidates) {
            if (!candidate || candidate.length < 1) {
                continue;
            }
            let minutes = OrderCalculator.totalProductionTimeMinutes(candidate);
            if (minutes > longestMinutes) {
                longest = candidate;
                longestMinutes = minutes;
            }
        }
        return longest;
    }
    /**
     * the products a combined product's slots offer to choose from
     * (hidden lines are left out, they can't be picked)
     */
    static choosableSubproductsOfProduct(product, getProductById) {
        let subproducts = [];
        if (!product || !getProductById || !product.slots || product.slots.length < 1) {
            return subproducts;
        }
        for (let slot of product.slots) {
            if (!slot || !slot.availableProducts) {
                continue;
            }
            for (let key in slot.availableProducts) {
                let slotItem = slot.availableProducts[key];
                if (!slotItem || slotItem.hidden) {
                    continue;
                }
                //the availableProducts map is keyed by product id, older entries
                //may only carry the key
                let subproduct = getProductById(slotItem.productId ? slotItem.productId : key);
                if (subproduct) {
                    subproducts.push(subproduct);
                }
            }
        }
        return subproducts;
    }
    /**
     * the products a combined cart item is actually made of: what the customer
     * picked in each of its slots
     */
    static chosenSubproductsOfCartItem(cartItem, product, getProductById) {
        let subproducts = [];
        if (!cartItem || !cartItem.chosen || !product || !getProductById || !product.slots || product.slots.length < 1) {
            return subproducts;
        }
        for (let slot of product.slots) {
            if (!slot) {
                continue;
            }
            //cartItem.chosen maps a slot id onto the picked product id (which is
            //also the key of the slot's availableProducts map)
            let chosenId = cartItem.chosen[slot.id];
            if (!chosenId) {
                continue;
            }
            let slotItem = slot.availableProducts ? slot.availableProducts[chosenId] : null;
            let subproduct = getProductById(slotItem && slotItem.productId ? slotItem.productId : chosenId);
            if (subproduct) {
                subproducts.push(subproduct);
            }
        }
        return subproducts;
    }
    /**
     * returns the production times that apply to a product as a normalized list of steps
     *
     * a combined product is ready when its slowest part is ready, so with a
     * getProductById resolver at hand the entries of the subproduct taking the
     * longest are returned (its own production times can't even be set in the
     * editor). without a resolver only its own entries are known.
     *
     * pass the ids the customer picked per slot (cartItem.chosen) to get the time
     * of the slowest chosen subproduct instead of the slowest choosable one.
     */
    static productionTimeEntriesForProduct(product, getProductById, chosenBySlotId, depth = 0) {
        if (!product) {
            return [];
        }
        let ownEntries = OrderCalculator.ownProductionTimeEntriesForProduct(product);
        if (product.type != ProductType.combined || !getProductById) {
            return ownEntries;
        }
        //a slot could point at another combined product: resolve a few levels deep,
        //but never follow a cycle
        if (depth > 3) {
            return ownEntries;
        }
        let chosenSubproducts = chosenBySlotId
            ? OrderCalculator.chosenSubproductsOfCartItem({ chosen: chosenBySlotId }, product, getProductById)
            : [];
        //nothing picked (yet): the menu can take as long as its slowest option
        let subproducts = chosenSubproducts.length > 0
            ? chosenSubproducts
            : OrderCalculator.choosableSubproductsOfProduct(product, getProductById);
        let candidates = [ownEntries];
        for (let subproduct of subproducts) {
            candidates.push(OrderCalculator.productionTimeEntriesForProduct(subproduct, getProductById, null, depth + 1));
        }
        return OrderCalculator.longestProductionTimeEntries(candidates);
    }
    /**
     * the production time entries of one cart item: for a combined product the ones
     * of the slowest subproduct it was put together from
     */
    productionTimeEntriesForCartItem(cartItem, product) {
        let getProductById = (productId) => this.company ? this.company.getProductById(productId) : null;
        return OrderCalculator.productionTimeEntriesForProduct(product, getProductById, cartItem ? cartItem.chosen : null);
    }
    /**
     * calculates when each item's production has time reserved in which time table
     * and derives the first possible pickup and delivery dates of the whole order
     *
     * the production time entries of a product run consecutively: the first entry
     * starts now, every following entry starts once the previous one is finished —
     * each fitted into its own time table (falling back to the opening hours when
     * missing), splitting across several time table windows where needed.
     *
     * a combined product takes as long as the slowest subproduct it was put
     * together from (see productionTimeEntriesForCartItem).
     *
     * the sales zone can define a minimal production time: if a product's entries
     * add up to less than the minimum the remainder is appended as an extra step.
     *
     * all dates are plain JS dates so the schedule can be stored on the order
     */
    getProductionSchedule(now) {
        let calculatedAt = now ? now : new Date();
        let salesZone = this.company.getSalesZoneById(this.orderRequest.zoneId);
        let minimalProductionTimeInMinutes = salesZone ? OrderCalculator.durationInMinutes(salesZone.minimalProductionTime) : 0;
        let schedule = new ProductionSchedule();
        schedule.calculatedAt = calculatedAt;
        let cartItems = this.orderRequest.cartItems ? this.orderRequest.cartItems : [];
        let productionEndDate = null;
        let allItemsScheduled = true;
        for (let cartItem of cartItems) {
            let product = this.company.getProductById(cartItem.productId);
            if (!product) {
                console.error('@OrderCalculator#getProductionSchedule: product not found', cartItem.productId);
                continue;
            }
            let entries = this.productionTimeEntriesForCartItem(cartItem, product);
            let totalEntryMinutes = OrderCalculator.totalProductionTimeMinutes(entries);
            if (minimalProductionTimeInMinutes > totalEntryMinutes) {
                entries.push({
                    minutes: minimalProductionTimeInMinutes - totalEntryMinutes,
                    timeTableId: entries.length > 0 ? entries[entries.length - 1].timeTableId : 'openingHours',
                    source: 'zone-minimum',
                });
            }
            if (entries.length < 1) {
                entries.push({ minutes: 0, timeTableId: 'openingHours', source: 'product' });
            }
            let scheduleItem = new ScheduleItem({
                cartItemId: cartItem.id || null,
                productId: cartItem.productId || null,
                productName: product.name || null,
                quantity: cartItem.quantity || 1,
            });
            let itemEndDate = null;
            for (let entry of entries) {
                let stepStartDate = itemEndDate ? itemEndDate : dayjs(calculatedAt);
                let fitted = this.fitDurationIntoTimeTable(entry.minutes, entry.timeTableId, true, stepStartDate);
                scheduleItem.steps.push(new ScheduleStep({
                    timeTableId: entry.timeTableId,
                    source: entry.source,
                    minutes: entry.minutes,
                    startDate: fitted.windows.length > 0 ? fitted.windows[0].start : (fitted.endDate ? fitted.endDate.toDate() : null),
                    endDate: fitted.endDate ? fitted.endDate.toDate() : null,
                    windows: fitted.windows,
                }));
                if (!fitted.endDate) {
                    itemEndDate = null;
                    break;
                }
                itemEndDate = fitted.endDate;
            }
            scheduleItem.productionEndDate = itemEndDate ? itemEndDate.toDate() : null;
            schedule.items.push(scheduleItem);
            if (!itemEndDate) {
                allItemsScheduled = false;
            }
            //the order is ready once the production of its last item is finished
            if (itemEndDate && (!productionEndDate || itemEndDate.isAfter(productionEndDate))) {
                productionEndDate = itemEndDate;
            }
        }
        //an item that couldn't be scheduled leaves the order without reliable dates
        //(its partial steps stay in the schedule for display purposes)
        if (!allItemsScheduled || !productionEndDate) {
            return schedule;
        }
        schedule.productionEndDate = productionEndDate.toDate();
        //pickup: the first moment on or after the production end within the chosen
        //pickup location's time table (the opening hours when no location limits it)
        let chosenPickupLocation = this.getChosenPickupLocation();
        let pickupTimeTableId = chosenPickupLocation && chosenPickupLocation.pickupTimeTableId ? chosenPickupLocation.pickupTimeTableId : 'openingHours';
        let pickupFit = this.fitDurationIntoTimeTable(0, pickupTimeTableId, true, productionEndDate);
        schedule.firstPossiblePickupDate = pickupFit.endDate ? pickupFit.endDate.toDate() : null;
        //delivery: the expected delivery time of the applicable zone is added —
        //zones limiting their delivery times to a time table have it fitted into that table,
        //zones without a limitation have it added directly
        if (this.orderRequest.deliveryType == DeliveryType.delivery) {
            let applicableDeliveryZone = this.getApplicableDeliveryZone();
            let deliveryZone = applicableDeliveryZone && applicableDeliveryZone.zone ? applicableDeliveryZone.zone : null;
            let deliveryTimeInMinutes = deliveryZone ? OrderCalculator.durationInMinutes(deliveryZone.averageDeliveryTime) : 0;
            if (!deliveryZone || !deliveryTimeInMinutes || deliveryTimeInMinutes <= 0) {
                schedule.firstPossibleDeliveryDate = productionEndDate.toDate();
            }
            else if (deliveryZone.deliveryTimeTableId) {
                let fitted = this.fitDurationIntoTimeTable(deliveryTimeInMinutes, deliveryZone.deliveryTimeTableId, true, productionEndDate);
                schedule.firstPossibleDeliveryDate = fitted.endDate ? fitted.endDate.toDate() : null;
                schedule.deliveryStep = new ScheduleStep({
                    timeTableId: deliveryZone.deliveryTimeTableId,
                    source: 'delivery',
                    minutes: deliveryTimeInMinutes,
                    startDate: fitted.windows.length > 0 ? fitted.windows[0].start : null,
                    endDate: fitted.endDate ? fitted.endDate.toDate() : null,
                    windows: fitted.windows,
                });
            }
            else {
                let deliveryEndDate = productionEndDate.add(deliveryTimeInMinutes, 'minutes');
                schedule.firstPossibleDeliveryDate = deliveryEndDate.toDate();
                schedule.deliveryStep = new ScheduleStep({
                    timeTableId: '',
                    source: 'delivery',
                    minutes: deliveryTimeInMinutes,
                    startDate: productionEndDate.toDate(),
                    endDate: deliveryEndDate.toDate(),
                    windows: [new ScheduleWindow({ start: productionEndDate.toDate(), end: deliveryEndDate.toDate(), minutes: deliveryTimeInMinutes })],
                });
            }
        }
        return schedule;
    }
}
